<script module>
    export { default as layout } from '../layouts/AppLayout.svelte';
</script>

<script lang="ts">
    import { useForm } from '@inertiajs/svelte';
    import { get } from 'svelte/store';
    import { fly } from 'svelte/transition';
    import ScoreAnswer from '@/actions/App/Actions/Answers/ScoreAnswer';
    import SkipAnswer from '@/actions/App/Actions/Answers/SkipAnswer';
    import ClearSkippedAnswers from '@/actions/App/Actions/Answers/ClearSkippedAnswers';

    let { answer = null, imageUrl = null, hasSkipped = false } = $props();
    let busy = $state(false);
    let transitionActive = $state(false);

    function onCardIntroEnd() {
        transitionActive = false;
        busy = false;
    }

    $effect(() => {
        answer;
        if (!transitionActive) {
            busy = false;
        }
    });

    function score(answerId: number, isCorrect: boolean) {
        busy = true;
        transitionActive = true;
        const form = useForm({ is_correct: isCorrect });
        get(form).patch(ScoreAnswer.url(answerId));
    }

    function skip(answerId: number) {
        busy = true;
        transitionActive = true;
        const form = useForm({});
        get(form).post(SkipAnswer.url(answerId));
    }

    function clearSkips() {
        transitionActive = false;
        const form = useForm({});
        get(form).post(ClearSkippedAnswers.url());
    }
</script>

<svelte:head>
    <title>Scoring — Case of the Day</title>
</svelte:head>

<h1 class="text-2xl font-bold text-gray-100 mb-6">Score Answers</h1>

    {#if !answer && !hasSkipped}
        <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-6 text-center text-gray-500">
            No answers pending scoring.
        </div>
    {:else if !answer && hasSkipped}
        <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-8 text-center">
            <p class="text-gray-400 mb-4">No more answers — you skipped some.</p>
            <button
                onclick={clearSkips}
                class="bg-indigo-600 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
            >
                Clear skips and start over
            </button>
        </div>
    {:else if answer}
        <div class="flex flex-col sm:flex-row sm:items-start sm:justify-center gap-4 max-w-4xl mx-auto">
            <div class="flex-1 min-w-0 relative">
                {#key answer.id}
                <div class="w-full" in:fly={{ y: 200, duration: 500, delay: 500 }} out:fly|local={{ y: -200, duration: 500 }} onintroend={onCardIntroEnd}>
                    <div class="bg-gray-900 rounded-lg shadow border border-gray-700 px-5 py-6 sm:p-8">
                        {#if answer.question.case?.topic || imageUrl}
                            <div class="flex items-center gap-3 mb-6 pb-4 border-b border-gray-800">
                                {#if imageUrl}
                                    <img
                                        src={imageUrl}
                                        alt=""
                                        class="w-16 h-16 rounded-lg object-cover shrink-0"
                                    />
                                {/if}
                                {#if answer.question.case.topic}
                                    <span class="text-sm font-medium text-cyan-400 bg-cyan-950 px-3 py-1 rounded-full">
                                        {answer.question.case.topic.name}
                                    </span>
                                {/if}
                            </div>
                        {/if}

                        <div class="mb-6">
                            <h2 class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Question</h2>
                            <!-- eslint-disable-next-line svelte/no-at-html-tags -- server-sanitized content -->
                            <div class="prose prose-invert prose-sm max-w-none">{@html answer.question.body}</div>
                        </div>

                        <div class="mb-6">
                            <h2 class="text-sm font-semibold text-cyan-400 uppercase tracking-wide mb-2">User's answer</h2>
                            <div class="bg-cyan-950 border border-cyan-800 rounded-lg p-4 text-lg text-gray-100 font-medium">
                                {answer.response_text}
                            </div>
                        </div>

                        {#if answer.question.correct_answer}
                            <div>
                                <h2 class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Expected answer</h2>
                                <!-- eslint-disable-next-line svelte/no-at-html-tags -- server-sanitized content -->
                                <div class="bg-green-950 border border-green-900 rounded-lg p-4 prose prose-invert prose-sm max-w-none">{@html answer.question.correct_answer}</div>
                            </div>
                        {/if}
                    </div>
                </div>
                {/key}
            </div>

            <div class="flex flex-row sm:flex-col gap-3 sm:gap-5 shrink-0">
                <button
                    onclick={() => score(answer.id, true)}
                    disabled={busy}
                    class="flex-1 h-14 sm:flex-initial sm:w-24 sm:h-24 bg-green-700 text-green-100 rounded-xl text-xs font-bold uppercase tracking-wide shadow-md hover:bg-green-600 hover:scale-105 transition-all flex flex-col items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:hover:scale-100"
                >
                    <svg class="size-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path opacity=".4" fill="currentColor" d="M0 256a256 256 0 1 0 512 0 256 256 0 1 0 -512 0zm135.1 7.1c9.4-9.4 24.6-9.4 33.9 0L221.1 315.2 340.5 151c7.8-10.7 22.8-13.1 33.5-5.3s13.1 22.8 5.3 33.5L243.4 366.1c-4.1 5.7-10.5 9.3-17.5 9.8s-13.9-2-18.8-7l-72-72c-9.4-9.4-9.4-24.6 0-33.9z"/><path fill="currentColor" d="M340.5 151c7.8-10.7 22.8-13.1 33.5-5.3s13.1 22.8 5.3 33.5L243.4 366.1c-4.1 5.7-10.5 9.3-17.5 9.8s-13.9-2-18.8-7l-72-72c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0L221.1 315.2 340.5 151z"/></svg>
                    Correct
                </button>
                <button
                    onclick={() => score(answer.id, false)}
                    disabled={busy}
                    class="flex-1 h-14 sm:flex-initial sm:w-24 sm:h-24 bg-red-700 text-red-100 rounded-xl text-xs font-bold uppercase tracking-wide shadow-md hover:bg-red-600 hover:scale-105 transition-all flex flex-col items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:hover:scale-100"
                >
                    <svg class="size-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path opacity=".4" fill="currentColor" d="M0 256a256 256 0 1 0 512 0 256 256 0 1 0 -512 0zm167-89c9.4-9.4 24.6-9.4 33.9 0l55 55 55-55c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-55 55 55 55c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0l-55-55-55 55c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l55-55-55-55c-9.4-9.4-9.4-24.6 0-33.9z"/><path fill="currentColor" d="M201 167c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l55 55-55 55c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l55-55 55 55c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-55-55 55-55c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-55 55-55-55z"/></svg>
                    Incorrect
                </button>
                <button
                    onclick={() => skip(answer.id)}
                    disabled={busy}
                    class="flex-1 h-14 sm:flex-initial sm:w-24 sm:h-24 bg-gray-800 text-gray-400 rounded-xl text-xs font-bold uppercase tracking-wide shadow-md hover:bg-gray-700 hover:text-gray-200 hover:scale-105 transition-all flex flex-col items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                    <svg class="size-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"><path opacity=".4" fill="currentColor" d="M64 72l0 368c0 16 9.6 30.5 24.3 36.8s31.8 3.2 43.4-7.9L304 303.7 304 208.3 131.7 43.1C120.1 32 103 28.9 88.3 35.2S64 56 64 72z"/><path fill="currentColor" d="M371.7 43.1C360.1 32 343 28.9 328.3 35.2S304 56 304 72l0 368c0 16 9.6 30.5 24.3 36.8s31.8 3.2 43.4-7.9l192-184c7.9-7.5 12.3-18 12.3-28.9s-4.5-21.3-12.3-28.9l-192-184z"/></svg>
                    Skip
                </button>
            </div>
        </div>
    {/if}
